import { useEffect, useRef } from 'react';
import { collection, doc, getDocs, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { isStudioPlaceholderDoc } from '../constants/studioProjects';
import { buildProjectMigrationPatch } from '../constants/projectTypes';

const MIGRATION_KEY = 'starling_studio_project_migration_v5';

/**
 * One-time per browser: backfill project defaults and remove deprecated fields.
 */
export function useStudioProjectMigration(userId) {
  const ranRef = useRef(false);

  useEffect(() => {
    if (!userId || ranRef.current) return undefined;
    let cancelled = false;

    (async () => {
      try {
        if (typeof window !== 'undefined' && window.sessionStorage?.getItem(MIGRATION_KEY) === '1') {
          ranRef.current = true;
          return;
        }
      } catch (_) {}

      ranRef.current = true;
      try {
        const colRef = collection(db, 'users', userId, 'studio');
        const snap = await getDocs(colRef);
        const ops = [];
        for (const d of snap.docs) {
          if (isStudioPlaceholderDoc(d)) continue;
          const data = d.data();
          const { patch, didChange } = buildProjectMigrationPatch(data);
          if (!didChange || Object.keys(patch).length === 0) continue;
          ops.push({ id: d.id, patch });
        }
        const CHUNK = 400;
        for (let i = 0; i < ops.length; i += CHUNK) {
          if (cancelled) return;
          const slice = ops.slice(i, i + CHUNK);
          const batch = writeBatch(db);
          slice.forEach(({ id, patch }) => {
            batch.update(doc(db, 'users', userId, 'studio', id), {
              ...patch,
              updatedAt: serverTimestamp(),
            });
          });
          await batch.commit();
        }
        try {
          window.sessionStorage?.setItem(MIGRATION_KEY, '1');
        } catch (_) {}
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn('[useStudioProjectMigration]', e);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId]);
}
